import React, { Component, Fragment } from 'react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import withStyles from '@mui/styles/withStyles';
import Typography from '../../component/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Grid from '../../component/form/Grid';
import GatedComponent from '../../component/GatedComponent';
import DataTable from '../../component/dataTable/DataTable';
import gql from 'graphql-tag';
import { joinDefined, prettyPhone } from '../../util/strings';
import BrandDot from '../../component/table/BrandDot';
import { getOfficeFromKey } from '../../util/brands';
import LinkButton from '../../component/form/LinkButton';
import { dayDelta, niceDateFromString } from '../../util/date';
import NewTransferModal from './NewTransferModal';
import { AddIcon, CloseIcon, EditIcon, ExclamationTriangleIcon, TickIcon } from '../../component/IconIndex';
import IconButton from '@mui/material/IconButton';
import { DotLegend } from '../../component/form/Legend';
import { theme } from '../../Theme';

class TransfersActive extends Component {
    state = {
        isTransferModalOpen: false,
        funeralID: null
    };
    refetch = null;
    applyRefetch = refetchFunc => {
        this.refetch = refetchFunc;
    };

    handleAddTransfer = row => {
        this.setState({ isTransferModalOpen: true, funeralID: row.ID });
    };

    getLegacyKeyFunc = (row, value) => {
        const { classes } = this.props;
        const office = getOfficeFromKey(value);
        const transferStates = [];

        //records created in the last 3 days are new
        if (
            (dayDelta(row.Created, new Date()) < 3 && !row.TransferActioned) ||
            row.Transfers.some(obj => !obj.TransferActioned && dayDelta(obj.Created, new Date()) < 3)
        ) {
            transferStates.push('New');
        }

        //records edited in the last 7 days are updated
        else if (
            (dayDelta(row.LastEdited, new Date()) < 7 && !row.TransferActioned) ||
            row.Transfers.some(obj => !obj.TransferActioned && dayDelta(obj.LastEdited, new Date()) < 7)
        ) {
            transferStates.push('Updated');
        }

        //records with dateofservice soon are urgent
        if (
            row.DateOfService &&
            dayDelta(row.DateOfService, new Date()) > -3 &&
            (!row.TransferActioned || row.Transfers.some(obj => !obj.TransferActioned))
        ) {
            transferStates.push('Urgent');
        }

        if (row.Certification.TypeOfBdmDeathCertificate === 'Coroner') {
            transferStates.push('Police Transfer');
        }

        return (
            <>
                {!!transferStates.length && (
                    <ul className={classes.dotList}>
                        {transferStates.map((transferState, i) => (
                            <li key={i} title={transferState}>
                                <DotLegend dotColor={this.getStateColor(transferState)} />
                            </li>
                        ))}
                    </ul>
                )}
                <GatedComponent isEnabledCode={'FM_ACCESS_Funeral_View'}>
                    {isEnabled =>
                        isEnabled ? (
                            <LinkButton href={'/funeral/' + value + '/' + row.ID + '/certification'}>
                                <BrandDot dotColour={office.color} label={<u>{value}</u>} title={office.label} />
                            </LinkButton>
                        ) : (
                            <BrandDot dotColour={office.color} label={value} title={office.label} />
                        )
                    }
                </GatedComponent>
            </>
        );
    };

    getStateColor = transferState => {
        switch (transferState.toLowerCase()) {
            case 'urgent':
                return theme.palette.legend.transfers.urgentColor;
            case 'new':
                return theme.palette.legend.transfers.newColor;
            case 'updated':
                return theme.palette.legend.transfers.updatedColor;
            case 'police transfer':
                return theme.palette.legend.transfers.policeTransferColor;
            default:
                return '';
        }
    };

    getCurrentBodyLocationFunc = row => {
        let location = '';
        if (!row.TransferActioned) {
            if (row.UsePlaceOfDeathAddress) {
                location =
                    'AddressBook' === row.PlaceOfDeathType && row.PlaceOfDeath.ID > 0 ? (
                        <>
                            {row.PlaceOfDeath.Name}
                            <br />
                            {prettyPhone(row.PlaceOfDeath.Phone, false)}
                            <br />
                            <small>{row.PlaceOfDeath.FullAddress}</small>
                        </>
                    ) : (
                        row.PlaceOfDeathResidentialAddress.LocationFlattened
                    );
            } else {
                location =
                    'AddressBook' === row.TransferFromType && row.TransferFrom.ID > 0 ? (
                        <>
                            {row.TransferFrom.Name}
                            <br />
                            {prettyPhone(row.TransferFrom.Phone, false)}
                            <br />
                            <small>{row.TransferFrom.FullAddress}</small>
                        </>
                    ) : (
                        row.TransferFromResidentialAddress.LocationFlattened
                    );
            }
        } else if (row.MortuaryLocation?.ID > 0) {
            location = (
                <>
                    {row.MortuaryLocation.Name}
                    <br />
                    {prettyPhone(row.MortuaryLocation.Phone, false)}
                    <br />
                    <small>{row.MortuaryLocation.FullAddress}</small>
                </>
            );
        } else {
            // fallback
            const prevTransfer = row.Transfers.find(row => !!row.TransferActioned);
            if (prevTransfer?.Destination?.ID > 0) {
                location = (
                    <>
                        {prevTransfer.Destination.Name}
                        <br />
                        {prettyPhone(prevTransfer.Destination.Phone, false)}
                        <br />
                        <small>{prevTransfer.Destination.FullAddress}</small>
                    </>
                );
            } else {
                const lastTransfer = row.Transfers[row.Transfers.length - 1];
                if (lastTransfer?.DepartureText) {
                    location = lastTransfer.DepartureText;
                }
            }
        }
        return location;
    };

    render() {
        const loading = false;
        const { classes } = this.props;
        const { isTransferModalOpen, funeralID } = this.state;
        return (
            <Fragment>
                <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                    Transfers
                    <small className={classes.header}>Body Locations</small>
                </Typography>
                {!!loading ? (
                    <span className={classes.loading}>
                        <CircularProgress />
                    </span>
                ) : (
                    this.renderBody()
                )}
                <NewTransferModal
                    funeralID={funeralID}
                    open={isTransferModalOpen}
                    onClose={(evt, doReload) => {
                        this.setState({ isTransferModalOpen: false, funeralID: null });
                        !!doReload && !!this.refetch && this.refetch();
                    }}
                />
            </Fragment>
        );
    }

    renderBody() {
        const { classes } = this.props;
        return (
            <Paper className={classes.paper} elevation={0}>
                <div className={classes.wideScroll}>
                    <GatedComponent showComponentCode={'FM_ACCESS_Transfers_View'}>
                        {() => this.renderTable()}
                    </GatedComponent>
                </div>
            </Paper>
        );
    }

    renderTable() {
        const { classes, history } = this.props;
        const showFlags = [
            {
                title: 'Created in the last 7 days',
                text: 'New',
                dotColor: theme.palette.legend.transfers.newColor
            },
            {
                title: 'Updated in the last 7 days',
                text: 'Updated',
                dotColor: theme.palette.legend.transfers.updatedColor
            },
            {
                title: 'Service in less than 3 days',
                text: 'Urgent',
                dotColor: theme.palette.legend.transfers.urgentColor
            },
            {
                title: 'Police Transfer / Coroner Involved',
                text: 'Police/Coroner',
                dotColor: theme.palette.legend.transfers.policeTransferColor
            }
        ];
        const columns = [
            {
                label: 'Key',
                propertyPath: 'LegacyKey',
                renderCell: this.getLegacyKeyFunc
            },
            {
                label: 'Deceased Name',
                propertyPath: 'Surname',
                renderCell: (row, value) => (
                    <>
                        {joinDefined([(value || '').toUpperCase(), row.FirstName, row.MiddleName], ' ')}
                        {row.AgeCode && 'Unknown' !== row.AgeCode && (
                            <small>
                                <br />
                                {row.Age || ''} {row.AgeCode}
                            </small>
                        )}
                    </>
                )
            },
            {
                label: 'Date of Death',
                propertyPath: 'DateOfDeath1',
                renderCell: (row, value) => (
                    <>
                        {niceDateFromString(value)}
                        <div className={classes.warn}>
                            {row.IsBariatric && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Bariatric
                                </div>
                            )}
                            {row.IsInfectious && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Infectious
                                </div>
                            )}
                            {row.IsDecomposed && (
                                <div>
                                    <ExclamationTriangleIcon />
                                    <br />
                                    Decomposed
                                </div>
                            )}
                        </div>
                    </>
                )
            },
            {
                label: 'Current Location',
                renderCell: this.getCurrentBodyLocationFunc
            },
            {
                label: 'Service',
                propertyPath: 'DateOfService',
                renderCell: (row, value) => (
                    <>
                        {niceDateFromString(value) || <i>(no date)</i>}
                        <br />
                        <small>{row.PlaceOfService.LocationFlattened}</small>
                    </>
                )
            },
            {
                label: 'Disposition',
                propertyPath: 'Disposal.Date',
                renderCell: (row, value) => (
                    <>
                        {niceDateFromString(value) || <i>(no date)</i>}
                        <br />
                        <small>{row.Disposal.LocationFlattened}</small>
                    </>
                )
            },
            {
                label: 'Inbound Actioned',
                tiny: true,
                renderCell: row =>
                    !!row.TransferActioned ? (
                        <TickIcon className={classes.iconFade} />
                    ) : (
                        <CloseIcon className={classes.iconGood} />
                    )
            },
            {
                label: 'Transfer Actioned',
                tiny: true,
                renderCell: row => {
                    if (!!row.Transfers.length) {
                        if (!!row.Transfers.some(obj => !obj.TransferActioned))
                            return <CloseIcon className={classes.iconGood} />;
                        return <TickIcon className={classes.iconGood} />;
                    }
                    if (!row.TransferActioned) return <CloseIcon className={classes.iconFade} />;
                    return <TickIcon className={classes.iconFade} />;
                }
            },
            {
                label: 'Assign',
                tiny: true,
                renderCell: row => {
                    const bits = { title: 'Add New Transfer...', className: classes.iconGood, icon: AddIcon };
                    if (!!row.TransferActioned && row.Transfers.every(obj => !!obj.TransferActioned)) {
                        bits.className = classes.iconFadeHover;
                    } else if (!row.Transfers.some(obj => !obj.TransferActioned)) {
                        bits.className = classes.iconGood;
                        bits.title = 'Add Inbound Transfer...';
                    } else {
                        bits.icon = EditIcon;
                        bits.title = 'Update Transfer...';
                    }
                    return (
                        <GatedComponent isEnabledCode={'FM_ACCESS_Transfers_View'}>
                            {isEnabled => (
                                <IconButton
                                    disabled={!isEnabled}
                                    title={bits.title}
                                    className={classes.iconButton}
                                    onClick={evt => {
                                        evt.stopPropagation();
                                        1 === 2 // TODO: implement transfer actions page
                                            ? history.push(`/transfers/actions/${row.LegacyKey}/${row.ID}`)
                                            : this.handleAddTransfer(row);
                                    }}
                                >
                                    <bits.icon className={bits.className} />
                                </IconButton>
                            )}
                        </GatedComponent>
                    );
                }
            }
        ];
        const variables = {
            limit: 50,
            sortBy: [
                { field: 'DateOfService', direction: 'ASC' }, // primary sort, empty & oldest first
                { field: 'DateOfDeath1', direction: 'ASC' }
            ],
            filters: [{ field: 'Cancelled', value: '0' }],
            filterBy: []
        };
        return (
            <Grid item>
                <Grid container spacing={3}>
                    <Grid item xs={12} className="sticky-table">
                        <DataTable
                            dataObject="Funeral"
                            queryName="readFunerals"
                            query={TransfersActiveQuery}
                            columns={columns}
                            variables={variables}
                            searchVariable="contains"
                            placeholder="Search by deceased name..."
                            noFiltersTitle="All Active Records"
                            isSortable={['LegacyKey', 'Surname', 'DateOfService', 'DateOfDeath1', 'Disposal.Date']}
                            brandProperty="LegacyKey"
                            showFlags={showFlags}
                            isFilterable={['Mortuary', 'Brand']}
                            onClickRow={this.handleAddTransfer}
                            getRefetchFunc={this.applyRefetch}
                        />
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

const TransfersActiveQuery = gql`
    fragment TransfersActive on Funeral {
        ID
        Created
        LastEdited
        LegacyKey
        FirstName
        MiddleName
        Surname
        DateOfDeath1
        Age
        AgeCode
        DateOfService
        IsBariatric
        IsInfectious
        IsDecomposed
        TransferActioned
        UsePlaceOfDeathAddress
        UseResidentialAddress
        TransferFromType
        PlaceOfDeathType
        TransferFrom {
            ID
            Name
            FullAddress
            Phone
        }
        TransferFromResidentialAddress {
            ID
            LocationFlattened
        }
        PlaceOfDeath {
            ID
            Name
            FullAddress
            Phone
        }
        PlaceOfDeathResidentialAddress {
            ID
            LocationFlattened
        }
        MortuaryLocation {
            ID
            Name
            Phone
            FullAddress
        }
        Disposal {
            ID
            CrematedOrBuried
            Date
            LocationFlattened
        }
        PlaceOfService {
            ID
            LocationFlattened
        }
        Transfers {
            ID
            DepartureText
            TransferActioned
            Destination {
                ID
                Name
                Phone
                FullAddress
            }
        }
        Certification {
            ID
            TypeOfBdmDeathCertificate
        }
    }

    query ReadTransfersActive(
        $limit: Int!
        $offset: Int!
        $sortBy: [SortField]
        $contains: String
        $filters: [Filters]
        $filterBy: [Filters]
    ) {
        readFunerals(
            transfer: false
            limit: $limit
            offset: $offset
            sortBy: $sortBy
            contains: $contains
            filters: $filters
            filterBy: $filterBy
        ) {
            edges {
                node {
                    ...TransfersActive
                }
            }
            pageInfo {
                hasNextPage
                hasPreviousPage
                totalCount
            }
        }
        SortDirection: __type(name: "FuneralSortFields") {
            name
            kind
            inputFields {
                name
                description
                type {
                    name
                    inputFields {
                        name
                        type {
                            name
                        }
                        __typename
                    }
                    enumValues {
                        name
                        description
                        __typename
                    }
                    __typename
                }
                __typename
            }
            __typename
        }
        FilterOptions: __type(name: "FuneralFilterFields") {
            name
            kind
            inputFields {
                name
                description
                type {
                    name
                    inputFields {
                        name
                        __typename
                    }
                    enumValues {
                        name
                        description
                        __typename
                    }
                    __typename
                }
                __typename
            }
            __typename
        }
        CustomFilters: __type(name: "FuneralCustomFilters") {
            name
            kind
            inputFields {
                name
                description
                type {
                    name
                    inputFields {
                        name
                        __typename
                    }
                    enumValues {
                        name
                        description
                        __typename
                    }
                    __typename
                }
                __typename
            }
            __typename
        }
    }
`;

const styles = ({ spacing, typography, palette, funeralHome }) => ({
    paper: {
        padding: spacing(3)
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    },
    header: {
        fontWeight: typography.fontWeightLight,
        marginLeft: 16,
        paddingLeft: 16,
        borderLeft: '1px solid ' + palette.action.active,
        color: palette.text.primary
    },
    dotList: {
        display: 'flex',
        margin: 0,
        padding: 0,
        listStyle: 'none'
    },
    warn: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 6,
        '& > div': {
            background: 'white',
            padding: `0 ${spacing(0.5)} ${spacing(0.5)}`,
            borderRadius: 5,
            fontVariantCaps: 'all-small-caps',
            fontSize: '0.875rem',
            lineHeight: 0.5,
            textAlign: 'center',
            color: theme.palette.validation.suggested,
            '& > svg': {
                color: theme.palette.validation.suggested + '!important'
            }
        }
    },
    iconFade: {
        margin: 'auto',
        display: 'block',
        fontSize: '2rem',
        color: 'rgba(0, 0, 0, 0.15)!important'
    },
    iconFadeHover: {
        margin: 'auto',
        display: 'block',
        fontSize: '2rem',
        '&:not(:hover)': {
            color: 'rgba(0, 0, 0, 0.15)!important'
        }
    },
    iconGood: {
        margin: 'auto',
        display: 'block',
        fontSize: '2rem'
    },
    iconButton: {
        margin: '-6px auto',
        display: 'block',
        '& > svg': {
            fontSize: '2rem'
        }
    }
});

export default compose(withRouter, withSnackbarMessage, withStyles(styles))(TransfersActive);
